import { useEffect, useRef, useState, useId, type CSSProperties } from 'react';
import { DayPicker, type Matcher } from 'react-day-picker';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import { Calendar as CalendarIcon, CircleAlert } from 'lucide-react';
import { cn } from './cn';
import 'react-day-picker/style.css';

export interface DatePickerProps {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  label?: React.ReactNode;
  placeholder?: string;
  error?: string | boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Earliest selectable date (inclusive). */
  minDate?: Dayjs | null;
  /** Latest selectable date (inclusive). */
  maxDate?: Dayjs | null;
  /** Display format for the trigger. Default DD.MM.YYYY. */
  displayFormat?: string;
  fullWidth?: boolean;
  className?: string;
}

// i18n uses 'cz', dayjs uses 'cs'. Map and fall back to sk.
const toDayjsLocale = (lng: string): string => {
  const base = (lng || 'sk').split('-')[0];
  return base === 'cz' ? 'cs' : base;
};

/**
 * Date field with a calendar popover — replaces MUI x-date-pickers <DatePicker />.
 * Built on react-day-picker, themed with our tokens; localized via dayjs formatters
 * (no date-fns dependency). value/onChange are dayjs objects.
 */
export function DatePicker({
  value,
  onChange,
  label,
  placeholder,
  error,
  helperText,
  required,
  disabled,
  minDate,
  maxDate,
  displayFormat = 'DD.MM.YYYY',
  fullWidth = true,
  className,
}: DatePickerProps) {
  const { i18n } = useTranslation();
  const lng = toDayjsLocale(i18n.language);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const hasError = Boolean(error);
  const errorText = typeof error === 'string' ? error : undefined;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = value ? value.toDate() : undefined;
  const disabledMatcher: Matcher[] = [];
  if (minDate) disabledMatcher.push({ before: minDate.startOf('day').toDate() });
  if (maxDate) disabledMatcher.push({ after: maxDate.endOf('day').toDate() });

  // Theme the react-day-picker CSS variables from our design tokens.
  const rdpStyle = {
    '--rdp-accent-color': 'rgb(var(--color-primary))',
    '--rdp-accent-background-color': 'rgb(var(--color-primary) / 0.15)',
    '--rdp-today-color': 'rgb(var(--color-primary))',
    '--rdp-day_button-border-radius': '10px',
    '--rdp-day-width': '40px',
    '--rdp-day-height': '40px',
    '--rdp-day_button-width': '38px',
    '--rdp-day_button-height': '38px',
  } as CSSProperties;

  return (
    <div
      ref={rootRef}
      className={cn('relative flex flex-col gap-1.5', fullWidth && 'w-full', className)}
    >
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-content">
          {label}
          {required && <span className="ml-0.5 text-primary">*</span>}
        </label>
      )}

      <button
        type="button"
        id={inputId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-11 items-center gap-2 rounded-xl border bg-surface px-3.5 text-left text-sm transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
          // Field-validation errors use the danger (red) token + icon (CONVENTIONS §5).
          hasError
            ? 'border-danger ring-2 ring-danger/25 focus-visible:border-danger focus-visible:ring-danger/40'
            : 'border-line hover:border-primary/40 focus-visible:border-primary',
          disabled && 'opacity-60 pointer-events-none',
        )}
      >
        <CalendarIcon size={18} className="shrink-0 text-muted" />
        <span className={cn('flex-1 truncate', value ? 'text-content' : 'text-muted/70')}>
          {value ? value.locale(lng).format(displayFormat) : placeholder || ''}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            role="dialog"
            className="absolute top-full z-[1400] mt-2 rounded-2xl border border-line bg-surface p-2 text-content shadow-pop"
          >
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={(d) => {
                onChange(d ? dayjs(d) : null);
                setOpen(false);
              }}
              defaultMonth={selected ?? (minDate ? minDate.toDate() : undefined)}
              disabled={disabledMatcher.length ? disabledMatcher : undefined}
              showOutsideDays
              weekStartsOn={1}
              style={rdpStyle}
              formatters={{
                formatCaption: (date) => dayjs(date).locale(lng).format('MMMM YYYY'),
                formatWeekdayName: (date) => dayjs(date).locale(lng).format('dd'),
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {errorText ? (
        <p className="flex items-start gap-1.5 text-xs font-medium text-danger">
          <CircleAlert size={13} strokeWidth={2.5} className="mt-px shrink-0" aria-hidden="true" />
          <span>{errorText}</span>
        </p>
      ) : helperText ? (
        <p className="text-xs text-muted">{helperText}</p>
      ) : null}
    </div>
  );
}
